import React, { useMemo, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import useSWR, { mutate } from "swr";
import axios from "axios";
import { formatIDR, formatDate, todayISO, daysBetween } from "@/lib/format";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Building2, Receipt, TrendingDown, Wallet, AlertCircle, Printer, Eye, ShieldOff, Upload, Send, ClipboardList } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const portalFetcher = (url) => axios.get(url).then((r) => r.data);

const BILL_STATUS = {
  unpaid: { label: "Belum Bayar", tone: "bg-warning/15 text-warning" },
  partial: { label: "Cicilan", tone: "bg-info/15 text-info" },
  paid: { label: "Lunas", tone: "bg-success/15 text-success" },
};

export default function TenantPortal() {
  const { token } = useParams();
  const { data, error, isLoading } = useSWR(
    token ? `${BACKEND_URL}/api/portal/${token}` : null,
    portalFetcher
  );
  const { data: proofs = [] } = useSWR(
    token ? `${BACKEND_URL}/api/portal/${token}/proofs` : null,
    portalFetcher
  );
  const [view, setView] = useState("bills"); // "bills" | "payments" | "proofs"
  const [proofFor, setProofFor] = useState(null); // bill

  if (isLoading) {
    return <PortalShell><div className="text-sm text-muted-foreground">Memuat portal...</div></PortalShell>;
  }

  if (error || !data) {
    return (
      <PortalShell>
        <Card className="border-dashed max-w-lg mx-auto">
          <CardContent className="p-10 text-center space-y-3">
            <ShieldOff className="h-10 w-10 mx-auto text-destructive" />
            <h2 className="font-display text-2xl font-medium">Portal tidak ditemukan</h2>
            <p className="text-sm text-muted-foreground">
              Link portal tidak valid atau sudah dinonaktifkan. Hubungi pemilik untuk mendapatkan link baru.
            </p>
          </CardContent>
        </Card>
      </PortalShell>
    );
  }

  const { tenant, contracts, units, bills, payments, totals } = data;
  const activeContract = contracts.find((c) => c.status === "active") || contracts[0];

  return (
    <PortalShell>
      <div className="space-y-6" data-testid="tenant-portal-page">
        {/* Hero */}
        <div className="rounded-lg border border-border/60 bg-card p-6 sm:p-8">
          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
            Portal Penyewa
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight mt-1" data-testid="portal-tenant-name">
            Halo, {tenant.name}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Ringkasan tagihan dan riwayat pembayaran Anda.
          </p>
        </div>

        {/* Metrics */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <MetricCard
            label="Sisa Tagihan"
            value={formatIDR(totals.outstanding)}
            hint={`${totals.unpaid_count} tagihan belum lunas`}
            icon={TrendingDown}
            tone={totals.outstanding > 0 ? "destructive" : "success"}
            testid="portal-outstanding"
          />
          <MetricCard
            label="Total Dibayar"
            value={formatIDR(totals.paid)}
            hint={`${payments.length} transaksi`}
            icon={Wallet}
            tone="success"
            testid="portal-paid"
          />
          <MetricCard
            label="Unit Sewa"
            value={activeContract ? units[activeContract.unit_id]?.code || "-" : "-"}
            hint={activeContract ? units[activeContract.unit_id]?.name : "Tidak ada kontrak aktif"}
            icon={Building2}
            testid="portal-unit"
          />
        </div>

        {/* Active contract detail */}
        {activeContract && (
          <Card>
            <CardContent className="p-6 space-y-3">
              <div className="flex items-center gap-2">
                <div className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">Kontrak Aktif</div>
                <Badge className="rounded-full bg-success/15 text-success text-[10px] uppercase tracking-wider">
                  {activeContract.status}
                </Badge>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
                <Info label="Unit" value={`${units[activeContract.unit_id]?.code || ""} — ${units[activeContract.unit_id]?.name || ""}`} />
                <Info label="Mulai" value={formatDate(activeContract.start_date)} />
                <Info label="Berakhir" value={formatDate(activeContract.end_date)} />
                <Info label="Sewa/bulan" value={formatIDR(activeContract.monthly_rent)} />
              </div>
              {units[activeContract.unit_id]?.address && (
                <div className="text-xs text-muted-foreground pt-1 border-t border-border/50">
                  {units[activeContract.unit_id].address}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Tabs */}
        <div className="flex items-center gap-2 border-b border-border/60 overflow-x-auto no-scrollbar">
          <TabBtn active={view === "bills"} onClick={() => setView("bills")} testid="portal-tab-bills">
            Tagihan ({bills.length})
          </TabBtn>
          <TabBtn active={view === "payments"} onClick={() => setView("payments")} testid="portal-tab-payments">
            Pembayaran ({payments.length})
          </TabBtn>
          <TabBtn active={view === "proofs"} onClick={() => setView("proofs")} testid="portal-tab-proofs">
            Bukti Transfer ({proofs.length})
          </TabBtn>
        </div>

        {view === "bills" ? (
          <BillsList bills={bills} units={units} onSendProof={setProofFor} />
        ) : view === "payments" ? (
          <PaymentsList payments={payments} token={token} />
        ) : (
          <ProofsList proofs={proofs} />
        )}

        {proofFor && (
          <ProofUploadDialog
            token={token}
            bill={proofFor}
            onClose={() => setProofFor(null)}
            onDone={() => {
              setProofFor(null);
              mutate(`${BACKEND_URL}/api/portal/${token}/proofs`);
              setView("proofs");
            }}
          />
        )}

        <footer className="pt-8 text-center text-xs text-muted-foreground">
          Portal read-only · Untuk pertanyaan hubungi pengelola langsung.
        </footer>
      </div>
    </PortalShell>
  );
}

function PortalShell({ children }) {
  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-10 glass-header">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-md bg-primary text-primary-foreground grid place-items-center font-display font-semibold text-sm">
              K
            </div>
            <div className="leading-none">
              <div className="font-display text-sm font-semibold tracking-tight">Aplikasi Kontrakan Surbakti</div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Portal Penyewa</div>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={() => window.print()} className="gap-1.5 no-print" data-testid="portal-print-button">
            <Printer className="h-3.5 w-3.5" /> Cetak
          </Button>
        </div>
      </header>
      <main className="mx-auto max-w-4xl px-4 sm:px-6 py-6 sm:py-10">{children}</main>
    </div>
  );
}

function MetricCard({ label, value, hint, icon: Icon, tone, testid }) {
  const toneCls =
    tone === "destructive" ? "bg-destructive/10 text-destructive" :
    tone === "success" ? "bg-success/10 text-success" :
    "bg-primary/10 text-primary";
  return (
    <Card data-testid={testid}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">{label}</div>
            <div className="font-display text-2xl sm:text-3xl font-medium tracking-tighter mt-2 tabular-nums truncate">{value}</div>
            {hint && <div className="text-xs text-muted-foreground mt-1 truncate">{hint}</div>}
          </div>
          <div className={`h-10 w-10 rounded-md grid place-items-center shrink-0 ${toneCls}`}>
            <Icon className="h-5 w-5" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function TabBtn({ active, onClick, children, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
        active ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"
      }`}
    >
      {children}
    </button>
  );
}

function BillsList({ bills, units, onSendProof }) {
  if (bills.length === 0) {
    return <Card><CardContent className="p-8 text-center text-sm text-muted-foreground">Belum ada tagihan.</CardContent></Card>;
  }
  return (
    <div className="space-y-2">
      {bills.map((b) => {
        const overdue = b.status !== "paid" && b.due_date < todayISO();
        const outstanding = Number(b.amount) + Number(b.late_fee || 0) - Number(b.paid_amount || 0);
        return (
          <Card key={b.id} className={overdue ? "border-destructive/40" : ""} data-testid={`portal-bill-${b.id}`}>
            <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge className="rounded-md font-mono">{units[b.unit_id]?.code || "?"}</Badge>
                  <Badge className={`rounded-full text-[10px] uppercase tracking-wider ${BILL_STATUS[b.status].tone}`}>
                    {BILL_STATUS[b.status].label}
                  </Badge>
                  {overdue && (
                    <Badge variant="destructive" className="rounded-full text-[10px] uppercase tracking-wider gap-1">
                      <AlertCircle className="h-3 w-3" /> Lewat {daysBetween(b.due_date, todayISO())} hari
                    </Badge>
                  )}
                </div>
                <div className="text-xs text-muted-foreground mt-1.5">
                  Periode {formatDate(b.period_start)} – {formatDate(b.period_end)} · Jatuh tempo {formatDate(b.due_date)}
                </div>
              </div>
              <div className="grid grid-cols-3 sm:flex sm:items-center gap-4 sm:gap-6 text-sm">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Tagihan</div>
                  <div className="font-medium tabular-nums">{formatIDR(b.amount)}</div>
                </div>
                {b.late_fee > 0 && (
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Denda</div>
                    <div className="font-medium tabular-nums text-destructive">{formatIDR(b.late_fee)}</div>
                  </div>
                )}
                <div>
                  <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Sisa</div>
                  <div className="font-medium tabular-nums text-primary">{formatIDR(outstanding)}</div>
                </div>
              </div>
              {b.status !== "paid" && (
                <Button size="sm" className="gap-1.5 shrink-0" onClick={() => onSendProof(b)} data-testid={`portal-send-proof-${b.id}`}>
                  <Upload className="h-3.5 w-3.5" /> Kirim Bukti
                </Button>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

function ProofsList({ proofs }) {
  if (proofs.length === 0) {
    return (
      <Card>
        <CardContent className="p-8 text-center text-sm text-muted-foreground flex flex-col items-center gap-2">
          <ClipboardList className="h-6 w-6" />
          Belum ada bukti transfer yang dikirim.
        </CardContent>
      </Card>
    );
  }
  return (
    <div className="space-y-2">
      {proofs.map((p) => {
        const tone =
          p.status === "confirmed" ? "bg-success/15 text-success" :
          p.status === "rejected" ? "bg-destructive/15 text-destructive" :
          "bg-warning/15 text-warning";
        const label = p.status === "confirmed" ? "Dikonfirmasi" : p.status === "rejected" ? "Ditolak" : "Menunggu";
        return (
          <Card key={p.id} data-testid={`portal-proof-${p.id}`}>
            <CardContent className="p-4 flex items-center justify-between gap-4">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <Badge className={`rounded-full text-[10px] uppercase tracking-wider ${tone}`}>{label}</Badge>
                  <span className="text-xs text-muted-foreground">{formatDate(p.paid_at)}</span>
                </div>
                <div className="font-medium mt-1">{formatIDR(p.amount)}</div>
                <div className="text-xs text-muted-foreground">{p.method === "cash" ? "Cash" : "Transfer"}</div>
                {p.review_note && (
                  <div className="text-xs text-muted-foreground italic mt-1">Review: {p.review_note}</div>
                )}
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

function ProofUploadDialog({ token, bill, onClose, onDone }) {
  const [amount, setAmount] = useState(
    (Number(bill.amount) + Number(bill.late_fee || 0) - Number(bill.paid_amount || 0)).toString()
  );
  const [method, setMethod] = useState("transfer");
  const [paidAt, setPaidAt] = useState(todayISO());
  const [note, setNote] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Pilih file bukti transfer."); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("bill_id", bill.id);
      fd.append("amount", amount);
      fd.append("method", method);
      fd.append("paid_at", paidAt);
      fd.append("note", note);
      await axios.post(`${BACKEND_URL}/api/portal/${token}/proofs`, fd);
      toast.success("Bukti terkirim. Menunggu konfirmasi pengelola.");
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal mengirim bukti");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Kirim Bukti Transfer</DialogTitle>
          <DialogDescription>Upload screenshot/foto bukti transfer. Pengelola akan mengkonfirmasi manual.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div
            onClick={() => fileRef.current?.click()}
            className="border-2 border-dashed rounded-md p-6 text-center cursor-pointer hover:border-primary/60 transition-colors"
            data-testid="proof-upload-dropzone"
          >
            <input
              ref={fileRef} type="file" accept="image/*,application/pdf" className="hidden"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              data-testid="proof-file-input"
            />
            {file ? (
              <div className="text-sm">
                <div className="font-medium truncate">{file.name}</div>
                <div className="text-xs text-muted-foreground">{(file.size / 1024).toFixed(0)} KB · Klik untuk ganti</div>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-2 text-muted-foreground">
                <Upload className="h-6 w-6" />
                <div className="text-sm">Klik untuk pilih file (JPG/PNG/PDF, maks 6MB)</div>
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Nominal</Label>
              <Input type="number" min="0" step="0.01" required value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="proof-amount-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Tanggal</Label>
              <Input type="date" value={paidAt} onChange={(e) => setPaidAt(e.target.value)} data-testid="proof-date-input" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Catatan (opsional)</Label>
            <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Bank asal, no. rekening, dll" />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Batal</Button>
            <Button type="submit" disabled={busy || !file} className="gap-1.5" data-testid="proof-submit-button">
              <Send className="h-4 w-4" /> {busy ? "Mengirim..." : "Kirim"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function PaymentsList({ payments, token }) {
  const [year, setYear] = useState("all");
  if (payments.length === 0) {
    return <Card><CardContent className="p-8 text-center text-sm text-muted-foreground">Belum ada pembayaran.</CardContent></Card>;
  }
  const years = Array.from(new Set(payments.map((p) => (p.paid_at || "").slice(0, 4)).filter(Boolean))).sort().reverse();
  const filtered = year === "all" ? payments : payments.filter((p) => (p.paid_at || "").startsWith(year));
  const totalFiltered = filtered.reduce((s, p) => s + Number(p.amount || 0), 0);
  const MONTH_ID = ["Jan","Feb","Mar","Apr","Mei","Jun","Jul","Ags","Sep","Okt","Nov","Des"];
  const monthLabel = (iso) => {
    if (!iso) return "";
    const [y, m] = iso.split("-");
    return `${MONTH_ID[parseInt(m,10)-1]} ${y}`;
  };
  return (
    <div className="space-y-3">
      {years.length > 1 && (
        <div className="flex items-center gap-2 flex-wrap" data-testid="portal-payments-filter">
          <button
            type="button"
            onClick={() => setYear("all")}
            className={`text-xs px-3 py-1 rounded-full border transition-colors ${year === "all" ? "bg-primary text-primary-foreground border-primary" : "border-border/60 hover:bg-muted"}`}
            data-testid="portal-year-all"
          >
            Semua ({payments.length})
          </button>
          {years.map((y) => {
            const count = payments.filter((p) => (p.paid_at || "").startsWith(y)).length;
            return (
              <button
                key={y}
                type="button"
                onClick={() => setYear(y)}
                className={`text-xs px-3 py-1 rounded-full border transition-colors ${year === y ? "bg-primary text-primary-foreground border-primary" : "border-border/60 hover:bg-muted"}`}
                data-testid={`portal-year-${y}`}
              >
                {y} ({count})
              </button>
            );
          })}
        </div>
      )}
      <div className="flex items-center justify-between text-xs text-muted-foreground px-1">
        <span>{filtered.length} transaksi{year !== "all" ? ` di tahun ${year}` : ""}</span>
        <span>Total: <span className="font-semibold text-foreground tabular-nums">{formatIDR(totalFiltered)}</span></span>
      </div>
      {filtered.length === 0 ? (
        <Card><CardContent className="p-8 text-center text-sm text-muted-foreground">Tidak ada pembayaran di tahun {year}.</CardContent></Card>
      ) : filtered.map((p) => (
        <Card key={p.id} data-testid={`portal-payment-${p.id}`}>
          <CardContent className="p-4 flex items-center justify-between gap-4">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                {p.unit_code && <Badge className="rounded-md font-mono text-[10px]">{p.unit_code}</Badge>}
                <span className="font-mono text-xs text-muted-foreground">{p.receipt_number}</span>
              </div>
              <div className="font-medium mt-1 tabular-nums">{formatIDR(p.amount)}</div>
              <div className="text-xs text-muted-foreground mt-0.5">
                {formatDate(p.paid_at)} · {p.method === "cash" ? "Cash" : "Transfer"}
                {p.period_start && (
                  <> · Untuk periode {monthLabel(p.period_start)}</>
                )}
              </div>
              {p.note && <div className="text-xs text-muted-foreground italic mt-1 truncate">{p.note}</div>}
            </div>
            <Link
              to={`/portal/${token}/receipt/${p.id}`}
              className="text-sm font-medium text-primary hover:underline flex items-center gap-1 shrink-0"
              data-testid={`portal-view-receipt-${p.id}`}
            >
              <Eye className="h-3.5 w-3.5" /> Lihat
            </Link>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">{label}</div>
      <div className="font-medium truncate">{value || "—"}</div>
    </div>
  );
}
